/* eslint-disable react/jsx-props-no-spreading */
import classnames from 'classnames';
import * as React from 'react';
import { useImperativeHandle, useRef } from 'react';
import type { SwiperOptions } from 'swiper/types';

import { SkipLinkScoped } from '../../../base/components/SkipLink';
import ItemGridWithLoading from '../../../ecommerce/components/ItemGrid/ItemGridWithLoading';
import useGaListPositionCalculation from '../../hooks/useGaListPositionCalculation';
import type { ImageTextPropertyBlockProps } from '../organisms/ImageTextPropertyBlock/ImageTextPropertyBlock';
import ImageTextPropertyBlock from '../organisms/ImageTextPropertyBlock/ImageTextPropertyBlock';
import { SwipableList } from '../SwipableList';
import { useGridBannerSize } from './ListBlock.hooks';
import styles from './ListBlock.module.scss';

export interface ListBlockProps<T> extends Omit<ListBlockOptions, 'lazyLoad'> {
    headerSlot?: React.ReactNode;
    items?: T[];
    renderItem: (item: T, index: number, listPosition: number | undefined) => React.ReactNode;
    itemUniqueKey: (item: T, index: number) => string;
    isLoading: boolean;
    cellsInRow: number;
    swiperOptions?: SwiperOptions;
    swiperMainClassName?: string;
    gridClasses?: string;
    skeletonCount?: number;
    renderSkeleton: () => React.ReactNode;
    skipLinkOptions?: {
        'aria-describedby'?: string;
    };
}

export interface ListBlockOptions {
    analyticsListName: string;
    useSwiper: boolean;
    lazyLoad: boolean;
    horizontalHidden: boolean;
    backgroundColor?:
        | ''
        | 'Background--light1Green2'
        | 'Background--light1Green3'
        | 'Background--light1Green4';
    banner?: ImageTextPropertyBlockProps;
}

export interface TeaserOptions {
    disableAsyncLink: boolean;
}

const ListBlock = React.forwardRef(
    <T, C extends React.ElementType = 'div'>(
        props: React.PolymorphicComponentPropsWithRef<C, ListBlockProps<T>>,
        ref: React.PolymorphicRef<C>,
    ) => {
        const listBlockRef = useRef<React.PolymorphicRef<C>>(null);

        const { isLoading, items, itemUniqueKey, renderSkeleton, cellsInRow, skeletonCount } =
            props;

        const { listPosition } = useGaListPositionCalculation(listBlockRef);

        useImperativeHandle(ref, () => listBlockRef.current);

        const renderItem = (product: T, index: number) =>
            props.renderItem(product, index, listPosition);

        const swiperOptions: SwiperOptions = {
            spaceBetween: 2,
            ...props.swiperOptions,
        };

        const bannerCellSize = useGridBannerSize(cellsInRow);

        const Component = props.as || 'div';

        return (
            <Component
                aria-labelledby={props['aria-labelledby']}
                role="group"
                ref={listBlockRef}
                className={classnames(
                    styles.Container,
                    props.backgroundColor &&
                        `${styles.Background} ${styles[props.backgroundColor]}`,
                )}
                data-list={props.analyticsListName}
            >
                {(isLoading || !!items?.length) && props.headerSlot}
                <SkipLinkScoped
                    skipLinkText="Hoppa över listan"
                    skipTargetText="Listan har hoppats över"
                    aria-describedby={props.skipLinkOptions?.['aria-describedby']}
                >
                    {props.useSwiper ? (
                        <div
                            className={classnames(
                                styles.ListContainer,
                                props.banner && styles.WithBanner,
                            )}
                        >
                            {props.banner && (
                                <div className={styles.Banner}>
                                    <ImageTextPropertyBlock {...props.banner} />
                                </div>
                            )}
                            <div className={styles.SwiperContainer}>
                                <SwipableList
                                    swiperMainClassName={classnames(
                                        props.swiperMainClassName,
                                        styles.Swiper,
                                    )}
                                    swiperPaginationClass={props.banner && styles.SwiperPagination}
                                    swiperOptions={swiperOptions}
                                >
                                    {!items || !items.length
                                        ? Array.from({ length: 8 }).map((_item, index) => (
                                              // eslint-disable-next-line react/no-array-index-key
                                              <React.Fragment key={index}>
                                                  {renderSkeleton()}
                                              </React.Fragment>
                                          ))
                                        : items.map((item, index) => (
                                              <React.Fragment
                                                  key={props.itemUniqueKey(item, index)}
                                              >
                                                  {renderItem(item, index)}
                                              </React.Fragment>
                                          ))}
                                </SwipableList>
                            </div>
                        </div>
                    ) : (
                        <ItemGridWithLoading
                            renderAtRowStart={(row) => {
                                if (row === 0) {
                                    return (
                                        props.banner && (
                                            <div className={`Grid-cell ${bannerCellSize}`}>
                                                <div
                                                    className={classnames(
                                                        styles.Banner,
                                                        styles.GridBanner,
                                                    )}
                                                >
                                                    <ImageTextPropertyBlock {...props.banner} />
                                                </div>
                                            </div>
                                        )
                                    );
                                }
                                return undefined;
                            }}
                            isLoading={isLoading}
                            items={items}
                            itemUniqueKey={itemUniqueKey}
                            renderItem={renderItem}
                            renderSkeleton={renderSkeleton}
                            horizontalHidden={props.horizontalHidden}
                            cellsInRow={cellsInRow}
                            gridClasses={classnames(props.gridClasses)}
                            skeletonCount={skeletonCount}
                        />
                    )}
                </SkipLinkScoped>
            </Component>
        );
    },
);

export default ListBlock;
